import { useMemo, useState } from 'react';
import { AlertCircle, Check, ChevronRight, Copy, Landmark, ShieldCheck } from 'lucide-react';
import VerdictBadge from '../analysis/VerdictBadge';
import ErrorMessage from '../common/ErrorMessage';
import { api } from '../../services/api';
import { continents, countries } from '../../services/countries';

const steps = ['Pays', 'Lettre', 'Succès'];
const fallbackAnalysis = { ref: 'SEN-DEMO', type: 'url', label: 'Analyse de démonstration', url: 'https://exemple-suspect.test', score: 87, verdict: 'danger', category: 'Lien suspect' };

function analysisTarget(analysis) {
  return analysis.url || analysis.label || analysis.sender?.email || 'Élément analysé';
}

function CountryFlag({ country, compact = false }) {
  const code = country.code.toLowerCase();
  const size = compact ? 'w-9 h-7 text-xs' : 'w-12 h-9 text-sm';
  return (
    <span className={`relative inline-flex ${size} items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 font-mono font-bold text-brand-navy shadow-sm`} title={`Drapeau ${country.name}`}>
      <span aria-hidden="true">{country.code}</span>
      <img
        src={`https://flagcdn.com/w80/${code}.png`}
        srcSet={`https://flagcdn.com/w160/${code}.png 2x`}
        alt={`Drapeau ${country.name}`}
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
        onError={(event) => { event.currentTarget.style.display = 'none'; }}
      />
    </span>
  );
}
function StepIndicator({ step }) {
  return (
    <div className="flex items-center px-4 py-4" aria-label="Étapes du signalement">
      {steps.map((label, index) => (
        <div key={label} className="flex-1 flex items-center">
          <div className="flex flex-col items-center gap-1">
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${index < step ? 'bg-verdict-safe text-white' : index === step ? 'bg-brand-navy text-white' : 'bg-gray-200 text-text-muted'}`}>{index < step ? <Check className="w-4 h-4" /> : index + 1}</span>
            <span className="text-[10px] text-text-muted">{label}</span>
          </div>
          {index < steps.length - 1 ? <div className="h-px bg-gray-200 flex-1 mx-2" /> : null}
        </div>
      ))}
    </div>
  );
}

function AnalysisSummary({ analysis }) {
  return (
    <section className="mx-4 mt-4 card border-l-4 border-l-accent-blue">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-text-subtle">Analyse liée</p>
          <p className="mt-1 font-mono text-sm text-text-primary break-all">{analysisTarget(analysis)}</p>
          <p className="mt-2 text-xs text-text-muted">{analysis.ref || 'Référence locale'} · Score {analysis.score}/100</p>
        </div>
        <VerdictBadge verdict={analysis.verdict || 'suspect'} size="sm" />
      </div>
    </section>
  );
}

export default function SignalementForm({ analysis = fallbackAnalysis, onDone }) {
  const [step, setStep] = useState(0);
  const [country, setCountry] = useState(countries[0]);
  const [dossier, setDossier] = useState(null);
  const [error, setError] = useState('');
  const currentAnalysis = analysis || fallbackAnalysis;

  const submitSignalement = async () => {
    setError('');
    try {
      const result = await api.sendSignalement({ analysis: currentAnalysis, country });
      setDossier(result);
      setStep(2);
    } catch {
      setError("Le signalement n'a pas pu être préparé. Réessayez dans quelques instants.");
    }
  };

  return (
    <div className="md:max-w-4xl md:mx-auto">
      <StepIndicator step={step} />
      {step < 2 ? <AnalysisSummary analysis={currentAnalysis} /> : null}
      {step === 0 ? <CountryStep country={country} setCountry={setCountry} next={() => setStep(1)} /> : null}
      {step === 1 ? <LetterStep analysis={currentAnalysis} country={country} next={submitSignalement} back={() => setStep(0)} error={error} /> : null}
      {step === 2 ? <SuccessStep country={country} dossier={dossier} onDone={onDone} /> : null}
    </div>
  );
}

function CountryStep({ country, setCountry, next }) {
  const [continent, setContinent] = useState('Tous');
  const visibleCountries = useMemo(() => countries.filter((item) => continent === 'Tous' || item.continent === continent), [continent]);
  return (
    <div className="px-4 mt-5">
      <h1 className="font-bold text-2xl text-brand-navy">Où signalez-vous ?</h1>
      <p className="text-sm text-text-muted mt-1">Sélectionnez le pays de l'infraction pour identifier l'autorité compétente.</p>
      <div className="mt-4 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none]" role="tablist" aria-label="Continents">
        <div className="flex gap-2 min-w-max">{continents.map((item) => <button key={item} type="button" onClick={() => setContinent(item)} aria-pressed={continent === item} className={`min-h-11 px-4 rounded-full text-sm font-semibold active:scale-95 transition-transform ${continent === item ? 'bg-brand-navy text-white' : 'bg-gray-200 text-text-muted'}`}>{item}</button>)}</div>
      </div>
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 max-h-[360px] overflow-y-auto pr-1">
        {visibleCountries.map((item) => <button key={item.id} type="button" onClick={() => setCountry(item)} aria-pressed={country.id === item.id} className={`card text-left border-2 active:scale-95 transition-transform ${country.id === item.id ? 'border-brand-navy ring-2 ring-brand-navy/10' : 'border-gray-200'}`}><CountryFlag country={item} /><p className="mt-3 font-bold text-sm text-text-primary">{item.name}</p><p className="text-xs text-text-muted truncate">{item.institution}</p><p className="mt-2 font-mono text-[10px] text-accent-blue">{item.continent} · {item.code}</p></button>)}
      </div>
      <div className="mt-4 card"><p className="text-[10px] text-accent-blue uppercase tracking-wider">Autorité pré-sélectionnée</p><div className="mt-2 flex gap-3"><CountryFlag country={country} /><Landmark className="w-8 h-8 text-brand-navy" /><div><p className="font-bold text-brand-navy">{country.institution}</p><p className="text-xs text-text-muted leading-relaxed">{country.name} · {country.sub}</p></div></div><div className="mt-4 rounded-xl bg-gray-50 p-3 flex gap-2 text-xs text-text-muted"><ShieldCheck className="w-4 h-4 text-verdict-safe" />Canal de signalement officiel et sécurisé.</div></div>
      <button type="button" onClick={next} className="btn-primary mt-5">Suivant : Rédiger le signalement <ChevronRight className="w-5 h-5" /></button>
    </div>
  );
}

function LetterStep({ analysis, country, next, back, error }) {
  return (
    <div className="px-4 mt-5">
      <h1 className="font-bold text-2xl text-brand-navy">Aperçu du signalement</h1>
      <p className="text-sm text-text-muted mt-1">Vérifiez les détails avant l'envoi officiel crypté.</p>
      <div className="card mt-5"><div className="flex justify-between items-start gap-3"><div><p className="text-[10px] text-text-subtle font-mono">RÉF : {analysis.ref || 'SEN-LOCAL'}</p><p className="text-xs text-text-muted mt-1">Destinataire : {country.institution}</p></div><span className="rounded-full bg-verdict-suspect-bg text-verdict-suspect px-2 py-1 text-[10px] font-semibold">Prêt à envoyer</span></div><div className="h-px bg-gray-100 my-4" /><p className="font-bold text-brand-navy">Objet : Signalement d'usage frauduleux de données personnelles</p><p className="mt-4 text-sm text-text-primary leading-relaxed">Par la présente, je porte à votre attention un incident numérique détecté par SENTINELLE. L'élément concerné est : <span className="font-mono">{analysisTarget(analysis)}</span>.</p><div className="mt-5 grid grid-cols-2 gap-3"><div className="rounded-xl bg-gray-50 p-3"><p className="text-[10px] text-text-subtle">URGENCE</p><p className="text-sm text-verdict-suspect">Modérée</p></div><div className="rounded-xl bg-gray-50 p-3"><p className="text-[10px] text-text-subtle">PREUVES</p><p className="text-sm text-brand-navy">Rapport PDF prêt</p></div></div></div>
      {error ? <div className="mt-4"><ErrorMessage message={error} /></div> : null}
      <button type="button" onClick={next} className="btn-primary mt-5">Confirmer et envoyer <ChevronRight className="w-5 h-5" /></button>
      <button type="button" onClick={back} className="btn-secondary mt-3">Modifier les détails</button>
    </div>
  );
}

function SuccessStep({ country, dossier, onDone }) {
  const ref = dossier?.ref || `SENT-8829-${country.code}`;
  const copyRef = () => navigator.clipboard?.writeText(ref);
  return (
    <div className="mx-4 mt-4 card text-center border-verdict-safe-border bg-verdict-safe-bg/40 md:max-w-xl md:mx-auto">
      <div className="w-20 h-20 rounded-full bg-verdict-safe text-white flex items-center justify-center mx-auto shadow-lg"><Check className="w-12 h-12" /></div>
      <h1 className="mt-6 font-extrabold text-2xl text-brand-navy">Signalement envoyé !</h1>
      <p className="mt-3 text-sm text-text-muted leading-relaxed">Votre dossier a été transmis avec succès à {country.institution} pour analyse immédiate.</p>
      <div className="mt-6 bg-white rounded-xl p-4"><p className="text-[10px] text-text-muted uppercase tracking-wider">Numéro de dossier</p><button type="button" onClick={copyRef} className="mt-2 mx-auto flex items-center justify-center gap-3 active:scale-95 transition-transform" aria-label="Copier le numéro de dossier"><span className="font-mono font-bold text-brand-navy text-xl">{ref}</span><Copy className="w-5 h-5 text-accent-blue" /></button></div>
      <div className="mt-4 rounded-xl bg-white p-3 text-xs text-text-muted flex items-center justify-center gap-2"><AlertCircle className="w-4 h-4" />L'autorité dispose de 30 jours pour répondre à votre requête.</div>
      <button type="button" onClick={onDone} className="btn-primary mt-6">Retour au Tableau de Bord</button>
    </div>
  );
}
